
"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type { PubSubMessage } from "@/types";
import { AppLogo } from "@/components/app/app-logo";
import { ConnectionForm, type ConnectionFormValues } from "@/components/app/connection-form";
import { FilterControls } from "@/components/app/filter-controls";
import { MessageList } from "@/components/app/message-list";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Settings2, Filter, ListChecks, Trash2 } from "lucide-react";
import { SidebarProvider, Sidebar, SidebarHeader, SidebarContent, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { listTopicsAction, createSubscriptionAction, pullMessagesAction, deleteSubscriptionAction } from "@/lib/pubsub-actions";

interface Topic {
  id: string;
  name: string;
}

const MESSAGE_PULL_INTERVAL = 3000; // 3 seconds

export default function Home() {
  const [projectId, setProjectId] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false); // For initial load or explicit refresh

  const [messages, setMessages] = useState<PubSubMessage[]>([]);
  const [filterKeyword, setFilterKeyword] = useState("");

  const { toast } = useToast();
  const messagePullIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearMessagePolling = () => {
    if (messagePullIntervalRef.current) {
      clearInterval(messagePullIntervalRef.current);
      messagePullIntervalRef.current = null;
    }
  };

  const fetchMessages = useCallback(async (currentSubscriptionName: string, currentProjectId: string) => {
    if (!currentSubscriptionName) return;
    // setIsLoadingMessages(true); // Optional: show loading for each poll
    const { messages: newMessages, error } = await pullMessagesAction(currentSubscriptionName, currentProjectId);
    // setIsLoadingMessages(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error Fetching Messages",
        description: error,
      });
      if (error.includes("not found")) { // Subscription might have expired
        handleDisconnect(true); // Force disconnect
      }
      return;
    }

    if (newMessages && newMessages.length > 0) {
      setMessages((prevMessages) => [...prevMessages, ...newMessages].slice(-200)); // Keep last 200 messages
    }
  }, [toast]); // Removed handleDisconnect from deps to avoid cycle, handle inside if needed

  useEffect(() => {
    if (isSubscribed && subscriptionName && projectId) {
      // Initial fetch
      fetchMessages(subscriptionName, projectId);
      // Start polling
      messagePullIntervalRef.current = setInterval(() => {
        fetchMessages(subscriptionName, projectId);
      }, MESSAGE_PULL_INTERVAL);
    } else {
      clearMessagePolling();
    }
    return () => clearMessagePolling();
  }, [isSubscribed, subscriptionName, projectId, fetchMessages]);

  const handleFetchTopics = async (currentProjectId: string) => {
    if (!currentProjectId) {
      toast({ variant: "destructive", title: "Project ID Required", description: "Please enter a GCP Project ID." });
      return;
    }
    setIsLoadingTopics(true);
    setTopics([]);
    setSelectedTopic(null);
    const { topics: fetchedTopics, error } = await listTopicsAction(currentProjectId);
    setIsLoadingTopics(false);

    if (error) {
      toast({ variant: "destructive", title: "Failed to Fetch Topics", description: error });
    } else if (fetchedTopics) {
      setTopics(fetchedTopics);
      if (fetchedTopics.length === 0) {
        toast({ title: "No Topics Found", description: `No Pub/Sub topics found in project "${currentProjectId}".` });
      } else {
         toast({ title: "Topics Loaded", description: `Found ${fetchedTopics.length} topics in project "${currentProjectId}".` });
      }
    }
  };

  const handleConnect = async (values: ConnectionFormValues) => {
    if (!values.projectId || !values.topicId) {
      toast({ variant: "destructive", title: "Missing Information", description: "Project ID and Topic ID are required." });
      return;
    }
    setIsLoadingSubscription(true);
    setMessages([]); // Clear previous messages

    const { subscriptionName: newSubName, error: subError } = await createSubscriptionAction(values.projectId, values.topicId);
    setIsLoadingSubscription(false);

    if (subError) {
      toast({ variant: "destructive", title: "Subscription Failed", description: subError });
    } else if (newSubName) {
      setSubscriptionName(newSubName);
      setSelectedTopic(values.topicId); // Store selected topic
      setProjectId(values.projectId); // Store project ID for message pulling
      setIsSubscribed(true);
      toast({
        title: "Subscribed!",
        description: `Successfully subscribed to topic. Subscription: ${newSubName.split('/').pop()}`,
      });
    }
  };

  const handleDisconnect = useCallback(async (silent = false) => {
    setIsLoadingSubscription(true); // Use same loading state for disconnect
    clearMessagePolling();
    
    if (subscriptionName) {
      const { error } = await deleteSubscriptionAction(subscriptionName, projectId);
      if (error && !silent) {
        toast({ variant: "destructive", title: "Failed to Delete Subscription", description: error });
      }
    }

    setIsSubscribed(false);
    setSubscriptionName(null);
    // Don't reset projectId and topics list, user might want to pick another topic
    // setSelectedTopic(null); 
    // setMessages([]); // Messages are cleared on connect
    
    if (!silent) {
      toast({
        title: "Disconnected",
        description: "You have been disconnected from the topic.",
      });
    }
    setIsLoadingSubscription(false);
  }, [subscriptionName, projectId, toast]);


  const clearMessages = () => {
    setMessages([]);
    toast({
      title: "Messages Cleared",
      description: "All current messages have been removed.",
    });
  };
  
  const currentTopicName = topics.find(t => t.id === selectedTopic)?.name || selectedTopic?.split('/').pop() || "N/A";
  const isLoading = isLoadingTopics || isLoadingSubscription;

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar className="border-r">
        <SidebarHeader className="p-0">
          <AppLogo />
        </SidebarHeader>
        <Separator />
        <SidebarContent className="p-0 flex flex-col">
          <div className="p-4 space-y-2">
            <h2 className="text-lg font-semibold flex items-center text-sidebar-foreground">
              <Settings2 className="mr-2 h-5 w-5" /> Connection
            </h2>
            <ConnectionForm
              projectId={projectId}
              setProjectId={setProjectId}
              topics={topics}
              selectedTopic={selectedTopic}
              setSelectedTopic={setSelectedTopic}
              isSubscribed={isSubscribed}
              isLoadingTopics={isLoadingTopics}
              isLoadingSubscription={isLoadingSubscription}
              onFetchTopics={handleFetchTopics}
              onConnect={handleConnect}
              onDisconnect={() => handleDisconnect(false)}
            />
          </div>
          <Separator />
          <div className="p-4 space-y-2">
            <h2 className="text-lg font-semibold flex items-center text-sidebar-foreground">
              <Filter className="mr-2 h-5 w-5" /> Filters
            </h2>
            <FilterControls
              filterKeyword={filterKeyword}
              setFilterKeyword={setFilterKeyword}
              disabled={!isSubscribed || isLoading}
            />
          </div>
           {isSubscribed && selectedTopic && (
            <div className="mt-auto p-4 border-t bg-muted/50">
              <p className="text-xs text-muted-foreground">
                Project: <span className="font-semibold text-primary">{projectId}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Topic: <span className="font-semibold text-primary">{currentTopicName}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Subscription: <span className="font-semibold text-primary">{subscriptionName?.split('/').pop()}</span>
              </p>
            </div>
          )}
        </SidebarContent>

      </Sidebar>
      <SidebarInset className="flex flex-col h-screen overflow-hidden">
        <header className="flex items-center justify-between p-4 border-b bg-card sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="h-8 w-8" />
            <h1 className="text-xl font-semibold flex items-center">
              <ListChecks className="mr-2 h-6 w-6 text-primary" />
              Messages
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearMessages}
            disabled={!isSubscribed || messages.length === 0 || isLoading}
            aria-label="Clear all messages"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Messages
          </Button>
        </header>
        <main className="flex-grow overflow-y-auto p-4">
           <MessageList
            messages={messages}
            filterKeyword={filterKeyword}
            isConnected={isSubscribed} 
            isLoadingConnection={isLoadingSubscription || isLoadingMessages}
          />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
